// src/features/wbs/lib/progressRollup.ts
import type { WbsFlatItem } from '../types';

function getWeight(r: WbsFlatItem) {
    const w = (r as any).actualWeight ?? (r as any).weight ?? 0;
    return Number.isFinite(w) ? Number(w) : 0;
}

function getPlanned(r: WbsFlatItem) {
    const v = r.plannedValue ?? 0;
    return Number.isFinite(v) ? Number(v) : 0;
}

function getActual(r: WbsFlatItem) {
    const v = (r as any).earnedValue ?? 0;
    return Number.isFinite(v) ? Number(v) : 0;
}

function clampPct(v: number) {
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(100, v));
}

function buildChildrenMap(rows: WbsFlatItem[]) {
    const map = new Map<string | null, WbsFlatItem[]>();
    for (const r of rows) {
        const key = r.parentId ?? null;
        const arr = map.get(key) ?? [];
        arr.push(r);
        map.set(key, arr);
    }
    return map;
}

function buildRowMap(rows: WbsFlatItem[]) {
    return new Map(rows.map((r) => [r.id, r]));
}

function isLeafRow(row: WbsFlatItem, childrenMap: Map<string | null, WbsFlatItem[]>) {
    const children = childrenMap.get(row.id) ?? [];
    return children.length === 0;
}

export function computeOverallProgress(rows: WbsFlatItem[]) {
    const childrenMap = buildChildrenMap(rows);

    let totalWeight = 0;
    let plannedSum = 0;
    let actualSum = 0;

    for (const r of rows) {
        if (!isLeafRow(r, childrenMap)) continue;

        const w = getWeight(r);
        if (w <= 0) continue;

        const pv = getPlanned(r);
        const ev = getActual(r);

        totalWeight += w;
        plannedSum += w * pv;
        actualSum += w * ev;
    }

    return {
        totalWeight,
        plannedPct: totalWeight > 0 ? clampPct(plannedSum / totalWeight) : 0,
        actualPct: totalWeight > 0 ? clampPct(actualSum / totalWeight) : 0,
    };
}

function rollupFromChildren(children: WbsFlatItem[]) {
    let totalWeight = 0;
    let plannedSum = 0;
    let actualSum = 0;

    for (const c of children) {
        const w = getWeight(c);
        if (w <= 0) continue;

        totalWeight += w;
        plannedSum += w * getPlanned(c);
        actualSum += w * getActual(c);
    }

    return {
        plannedValue: totalWeight > 0 ? clampPct(plannedSum / totalWeight) : 0,
        earnedValue: totalWeight > 0 ? clampPct(actualSum / totalWeight) : 0,
    };
}

export function buildRollupRows(rows: WbsFlatItem[]) {
    const rowMap = buildRowMap(rows);
    const childrenMap = buildChildrenMap(rows);

    const memo = new Map<string, { plannedValue: number; earnedValue: number }>();

    function dfs(rowId: string): { plannedValue: number; earnedValue: number } {
        if (memo.has(rowId)) return memo.get(rowId)!;

        const row = rowMap.get(rowId);
        if (!row) {
            const empty = { plannedValue: 0, earnedValue: 0 };
            memo.set(rowId, empty);
            return empty;
        }

        const children = childrenMap.get(rowId) ?? [];
        if (children.length === 0) {
            const leaf = {
                plannedValue: getPlanned(row),
                earnedValue: getActual(row),
            };
            memo.set(rowId, leaf);
            return leaf;
        }

        const childRows = children.map((c) => {
            const rolled = dfs(c.id);
            return {
                ...c,
                plannedValue: rolled.plannedValue,
                earnedValue: rolled.earnedValue,
            } as WbsFlatItem;
        });

        const rolled = rollupFromChildren(childRows);
        memo.set(rowId, rolled);
        return rolled;
    }

    return rows.map((r) => {
        const rolled = dfs(r.id);
        return {
            ...r,
            plannedValue: rolled.plannedValue,
            earnedValue: rolled.earnedValue,
        };
    });
}

export function computeTopLevelProgress(rows: WbsFlatItem[]) {
    const childrenMap = buildChildrenMap(rows);
    const rowMap = buildRowMap(rows);

    const rolledRows = buildRollupRows(rows);
    const rolledMap = new Map(rolledRows.map((r) => [r.id, r]));

    const topRows = rows.filter((r) => !r.parentId);
    const overall = computeOverallProgress(rows);
    const totalWeight = overall.totalWeight || 1;

    function collectLeafWeights(rowId: string): number {
        const children = childrenMap.get(rowId) ?? [];
        if (children.length === 0) {
            const row = rowMap.get(rowId);
            return row ? getWeight(row) : 0;
        }
        return children.reduce((s, c) => s + collectLeafWeights(c.id), 0);
    }

    return topRows.map((top) => {
        const rolled = rolledMap.get(top.id)!;
        const ownWeight = collectLeafWeights(top.id);

        return {
            id: top.id,
            code: top.code,
            title: top.name,
            weightPct: (ownWeight / totalWeight) * 100,
            pvPct: rolled.plannedValue ?? 0,
            evPct: rolled.earnedValue ?? 0,
        };
    });
}
